import { useState } from "react";
import { DeleteOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import {
  Control,
  UseFormSetValue,
  useFormState,
  useWatch,
} from "react-hook-form";
import { normalizeFilestorePath } from "@entities/document";
import { DocumentFileRow } from "@entities/journal";
import { SectionThirdFormType } from "../../../../model/types";
import { DocTypeEnum } from "../../../../model/types/document-types";
import { InfoDropdown } from "../info-dropdown/info-dropdown";
import { SectionDocumentModal } from "../section-document-modal/section-document-modal";

interface SectionDocumentItemProps {
  title: string;
  docType: DocTypeEnum;
  setValue?: UseFormSetValue<SectionThirdFormType>;
  isInfo?: boolean;
  control: Control<SectionThirdFormType>;
  checkErrorEnum?: DocTypeEnum;
  disabled?: boolean;
}

export const SectionDocumentItem = ({
  title,
  docType,
  setValue,
  control,
  isInfo,
  checkErrorEnum,
  disabled = false,
}: SectionDocumentItemProps) => {
  const { errors } = useFormState({
    control,
    name: ["docs"],
  });

  const allDocs = useWatch({
    control,
    name: "docs",
  }) || [];

  const documents = allDocs.filter((doc) => doc.docType === docType);

  const [isModalVisible, setIsModalVisible] = useState(false);

  const updateFormDocs = (updatedDocsForThisType: any[]) => {
    if (!setValue) return;
    const withoutCurrentType = allDocs.filter((doc) => doc.docType !== docType);
    const merged = [...withoutCurrentType, ...updatedDocsForThisType];
    setValue("docs", merged, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleDelete = (id: string) => {
    const updated = documents.filter((doc: any) => doc.id !== id);
    const updatedDocsForThisType = updated.map((doc: any) => ({
      docId: doc.docId || doc.id,
      docType,
    }));
    updateFormDocs(updatedDocsForThisType);
  };

  const hasErrors = allDocs.some((doc) => doc.docType === checkErrorEnum);
  const showError = !hasErrors && !!checkErrorEnum && errors?.docs;

  return (
    <div className="document-container">
      <div className="document-description">{title}</div>

      {documents.map((doc: any) => (
        <div className="document-item" key={doc?.id}>
          <div className="document-name">
            <div className="document-name-container">
              <DocumentFileRow
                className="document-name-container-title-row"
                linkClassName="document-name-container-title"
                fileName={doc?.title || doc?.docTitle}
                documentUrl={
                  doc?.originalData?.documentUrl || doc?.documentUrl
                    ? normalizeFilestorePath(
                        doc?.originalData?.documentUrl || doc?.documentUrl || "",
                      )
                    : undefined
                }
                actions={
                  <>
                    <Tooltip title="Информация о документе">
                      <div className="journal-document-info-trigger">
                        <InfoDropdown docType={docType} docId={doc?.docId || doc?.id} />
                      </div>
                    </Tooltip>
                    {!isInfo && !disabled && (
                      <Tooltip title="Удалить">
                        <DeleteOutlined onClick={() => handleDelete(doc.id)} />
                      </Tooltip>
                    )}
                  </>
                }
              />
              <div className="document-name-container-description">
                {doc?.originalData?.searchField}
              </div>
            </div>
          </div>
        </div>
      ))}

      {!isInfo && !disabled && (
        <div className="document-add-button">
          <Button type="dashed" onClick={() => setIsModalVisible(true)}>
            Добавить
          </Button>
        </div>
      )}
      {showError && (
        <div className="journal-document-validation-error">
          Добавьте документы
        </div>
      )}

      <SectionDocumentModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        docType={docType}
        onConfirm={(selectedDocs) => {
          const newDocs = selectedDocs
            .filter((doc: any) => !documents.find((d: any) => d.id === doc.key))
            .map((doc: any) => ({
              ...doc,
              id: doc.key,
              docId: doc.docId || doc.key,
              docType,
            }));

          updateFormDocs([...documents, ...newDocs]);
          setIsModalVisible(false);
        }}
        key={documents.length}
      />
    </div>
  );
};
